import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import api from '../../utils/api';
import { getBuildingNameClass } from '../../utils/ssDueColor';
import { daysUntilDue } from '../../utils/dueDate';
import { Search, Building2, X, RotateCcw, CalendarClock, Ban } from 'lucide-react';
import BuildingPageHeading from '../../components/BuildingPageHeading';
import toast from 'react-hot-toast';

const SCOPE_IDS = new Set(['all', 'full', 'ss_only']);

function confirmClassifyBuilding(buildingName, status) {
  const name = buildingName || 'this building';
  if (status === 'DEFERRED') {
    return window.confirm(
      `Starts later: "${name}"?\n\n` +
        `Not deleted — hidden from All / Due / Paperwork / Schedule.\n` +
        `Resume defaults to 1 January next year.\n` +
        `Restore anytime from Buildings → Starts later.`
    );
  }
  return window.confirm(
    `Ended: "${name}"?\n\n` +
      `Not deleted — contract finished; hidden from work queues.\n` +
      `Restore anytime from Buildings → Ended.`
  );
}

function naturalNameCompare(a, b) {
  return String(a || '').localeCompare(String(b || ''), undefined, {
    numeric: true,
    sensitivity: 'base',
  });
}

function scopeBadge(scope) {
  if (scope === 'FULL_AGENT') {
    return (
      <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded bg-emerald-100 text-emerald-800">
        Full agent
      </span>
    );
  }
  if (scope === 'SS_ONLY') {
    return (
      <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded bg-violet-100 text-violet-800">
        Partly SS
      </span>
    );
  }
  return (
    <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded bg-slate-100 text-slate-600">
      Other
    </span>
  );
}

function BuildingInfoPanel({ building, onClose, onOpen }) {
  if (!building) return null;
  const days = daysUntilDue(building.due_date);
  const partners = building.partner_companies || [];
  const ssCodes = building.ss_only_codes || [];

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/30" onClick={onClose}>
      <aside
        className="w-full max-w-md h-full bg-white shadow-xl overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-white border-b px-5 py-4 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className={`text-xl font-bold truncate ${getBuildingNameClass(days, building.application_approved)}`}>
              {building.name}
            </h2>
            <div className="mt-1">{scopeBadge(building.agency_scope)}</div>
          </div>
          <button type="button" onClick={onClose} className="p-2 text-gray-400 hover:text-gray-700">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-5 text-sm">
          <section>
            <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Building</h3>
            <dl className="space-y-2">
              <div>
                <dt className="text-gray-500">Address</dt>
                <dd className="font-medium text-gray-900">{building.address || '—'}</dd>
              </div>
              <div>
                <dt className="text-gray-500">BWOF due</dt>
                <dd className="font-medium text-gray-900">
                  {building.due_date ? new Date(building.due_date).toLocaleDateString() : '—'}
                </dd>
              </div>
              {building.compliance_schedule_number && (
                <div>
                  <dt className="text-gray-500">CS number</dt>
                  <dd className="font-medium text-gray-900">{building.compliance_schedule_number}</dd>
                </div>
              )}
              <div>
                <dt className="text-gray-500">Status</dt>
                <dd className="font-medium text-gray-900">
                  {building.status === 'INACTIVE' ? 'Inactive' : building.status || '—'}
                </dd>
              </div>
            </dl>
          </section>

          <section>
            <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
              Owner / company contacts
            </h3>
            <dl className="space-y-2">
              <div>
                <dt className="text-gray-500">Owner / client</dt>
                <dd className="font-medium text-gray-900">{building.owner_name || '—'}</dd>
              </div>
              {(building.owner_email || building.owner_phone) && (
                <div>
                  <dt className="text-gray-500">Contact</dt>
                  <dd className="font-medium text-gray-900">
                    {[building.owner_email, building.owner_phone].filter(Boolean).join(' · ')}
                  </dd>
                </div>
              )}
            </dl>
          </section>

          <section>
            <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
              Service companies (mandates)
            </h3>
            {partners.length === 0 && building.agency_scope === 'FULL_AGENT' ? (
              <p className="text-gray-700">
                Full BWOF agent building
                {building.is_bwof_agent ? ' (our agency flag on).' : '.'} Partner companies appear when linked under
                Service mandates.
              </p>
            ) : partners.length === 0 ? (
              <p className="text-gray-500">No partner company linked yet.</p>
            ) : (
              <ul className="space-y-2">
                {partners.map((name) => (
                  <li key={name} className="rounded-lg border border-violet-100 bg-violet-50 px-3 py-2 font-medium text-violet-950">
                    {name}
                  </li>
                ))}
              </ul>
            )}
            {ssCodes.length > 0 && (
              <p className="mt-2 text-xs text-gray-600">SS assigned: {ssCodes.join(', ')}</p>
            )}
          </section>

          <div className="pt-2 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => onOpen(building.id)}
              className="flex-1 px-4 py-2.5 rounded-lg bg-primary-600 text-white font-semibold hover:bg-primary-700"
            >
              Open building
            </button>
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2.5 rounded-lg border border-gray-300 text-gray-700 font-semibold hover:bg-gray-50"
            >
              Close
            </button>
          </div>
        </div>
      </aside>
    </div>
  );
}

function Buildings() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const dueSoonFromUrl = searchParams.get('dueSoon') === '1';
  const trashFromUrl = searchParams.get('trash') === '1' || searchParams.get('inactive') === '1';
  const contractParam = String(searchParams.get('contract') || '').toLowerCase();
  const contractFilter =
    contractParam === 'deferred' || contractParam === 'later'
      ? 'DEFERRED'
      : contractParam === 'ended'
        ? 'ENDED'
        : '';
  const scopeFromUrl = searchParams.get('scope') || 'all';
  const scope = SCOPE_IDS.has(scopeFromUrl) ? scopeFromUrl : 'all';
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const showInactive = trashFromUrl && !dueSoonFromUrl && !contractFilter;
  const showShelved = !!contractFilter && !dueSoonFromUrl;

  const {
    data: allData,
    isLoading: allLoading,
    isError: allError,
    error: allErrorDetail,
    refetch: refetchBuildings,
  } = useQuery({
    queryKey: ['buildings', showInactive ? 'inactive' : 'active'],
    queryFn: async () => {
      const params = { limit: 5000 };
      if (showInactive) params.status = 'INACTIVE';
      const response = await api.get('/buildings', { params });
      return response.data;
    },
    enabled: !dueSoonFromUrl && !showShelved,
    retry: 1,
  });

  const { data: shelvedData, isLoading: shelvedLoading } = useQuery({
    queryKey: ['buildings', 'contract-shelved', contractFilter],
    queryFn: async () => {
      const response = await api.get('/buildings/contract-shelved', {
        params: { status: contractFilter },
      });
      return response.data?.data?.items || response.data?.items || [];
    },
    enabled: showShelved,
  });

  const { data: dueSoonData, isLoading: dueSoonLoading } = useQuery({
    queryKey: ['buildings', 'due-soon', 30],
    queryFn: async () => {
      const response = await api.get('/buildings/due-soon?days=30');
      return response.data.data || [];
    },
    enabled: dueSoonFromUrl,
  });

  const restoreMutation = useMutation({
    mutationFn: async (buildingId) => {
      const res = await api.post(`/buildings/${buildingId}/restore`);
      return res.data;
    },
    onSuccess: (data) => {
      toast.success(`Active again: ${data.name || 'Building'}`);
      queryClient.invalidateQueries(['buildings']);
    },
    onError: (err) => {
      toast.error(err.response?.data?.error || 'Failed to set building Active');
    },
  });

  const [classifyBusy, setClassifyBusy] = useState(null);
  const classifyMutation = useMutation({
    mutationFn: async ({ buildingId, status }) =>
      (await api.put(`/buildings/${buildingId}/contract-status`, { status })).data,
    onSuccess: (_data, vars) => {
      const label =
        vars.status === 'ACTIVE'
          ? 'Restored'
          : vars.status === 'DEFERRED'
            ? 'Starts later'
            : 'Ended';
      toast.success(
        vars.status === 'ACTIVE'
          ? 'Restored — back in All / Due / Paperwork'
          : `${label} — hidden from All / Due / Paperwork`
      );
      queryClient.invalidateQueries({ queryKey: ['buildings'] });
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
      setSelected(null);
    },
    onError: (err) => {
      toast.error(err.response?.data?.error || 'Classification failed');
    },
    onSettled: () => setClassifyBusy(null),
  });

  const fromDashboard = dueSoonFromUrl;
  const rawList = dueSoonFromUrl
    ? Array.isArray(dueSoonData)
      ? dueSoonData
      : []
    : allData?.data || [];
  const isLoading = dueSoonFromUrl ? dueSoonLoading : showShelved ? shelvedLoading : allLoading;
  const shelvedItems = useMemo(() => {
    let list = Array.isArray(shelvedData) ? [...shelvedData] : [];
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter(
        (r) =>
          String(r.building_name || '')
            .toLowerCase()
            .includes(q) ||
          String(r.building_address || '')
            .toLowerCase()
            .includes(q)
      );
    }
    return list;
  }, [shelvedData, search]);

  const buildings = useMemo(() => {
    let list = [...(rawList || [])];
    if (!dueSoonFromUrl && !showInactive) {
      if (scope === 'full') list = list.filter((b) => b.agency_scope === 'FULL_AGENT');
      else if (scope === 'ss_only') list = list.filter((b) => b.agency_scope === 'SS_ONLY');
    }
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter(
        (b) =>
          String(b.name || '')
            .toLowerCase()
            .includes(q) ||
          String(b.address || '')
            .toLowerCase()
            .includes(q) ||
          String(b.owner_name || '')
            .toLowerCase()
            .includes(q) ||
          (b.partner_companies || []).some((p) => String(p).toLowerCase().includes(q)) ||
          (b.ss_only_codes || []).some((c) => String(c).toLowerCase().includes(q))
      );
    }
    list.sort((a, b) => naturalNameCompare(a.name, b.name));
    return list;
  }, [rawList, dueSoonFromUrl, showInactive, scope, search]);

  const clearDashboardFilter = () => {
    setSearchParams({});
  };

  return (
    <div>
      <BuildingPageHeading
        title={
          showShelved
            ? contractFilter === 'ENDED'
              ? 'Ended (contract)'
              : 'Starts later'
            : showInactive
              ? 'Inactived list'
              : 'Buildings'
        }
        infoTitle="Buildings"
        infoChildren={
          showShelved ? (
            <p>
              Building list — not deleted. <strong>Starts later</strong> = contract not yet active / operation
              start in the future; <strong>Ended</strong> = contract finished. <strong>Active</strong> on a
              row puts that building back into the working portfolio.
            </p>
          ) : showInactive ? (
            <p>
              Buildings set to Inactive are hidden from the portfolio and apps. All data stays in the database.
              Use <strong>Active</strong> to show a building again. Switch lists from the header sub-menu.
            </p>
          ) : (
            <p>
              Buildings portfolio. Use the header sub-menu for All / Full agent / Partly SS only / New building /
              Starts later / Ended / Inactived list. Operation start and contract termination are set on{' '}
              <strong>Edit Building</strong>.
            </p>
          )
        }
      />

      {fromDashboard && (
        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg flex items-center justify-between flex-wrap gap-2">
          <p className="text-sm text-blue-800">Showing only buildings due soon (within 60 days).</p>
          <button
            type="button"
            onClick={clearDashboardFilter}
            className="text-sm font-medium text-primary-600 hover:text-primary-800"
          >
            Show all buildings
          </button>
        </div>
      )}

      {showShelved && (
        <div className="mb-6 p-4 bg-sky-50 border border-sky-200 rounded-lg text-sm text-sky-950">
          {contractFilter === 'ENDED'
            ? 'Ended buildings — hidden from Due / Urgent. Active on a row brings that building back.'
            : 'Starts later — one row per building. Active on a row brings that building back now.'}
        </div>
      )}

      {showInactive && (
        <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-950">
          These buildings are Inactive (hidden). Nothing was deleted. Use <strong>Active</strong> to bring one back.
        </div>
      )}

      <div className="mb-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder={
              showShelved
                ? 'Search by building name or address…'
                : showInactive
                  ? 'Search inactive by name, address, owner…'
                  : 'Search buildings by name, address, owner…'
            }
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>
      </div>

      {!dueSoonFromUrl && !showShelved && allError ? (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-6 text-center text-amber-950">
          <p className="font-medium mb-1">Could not load buildings</p>
          <p className="text-sm text-amber-900/90 mb-4">
            The API request failed (check MySQL and that the API is running).
            {allErrorDetail?.message ? (
              <span className="block mt-2 font-mono text-xs opacity-90">{allErrorDetail.message}</span>
            ) : null}
          </p>
          <button
            type="button"
            onClick={() => refetchBuildings()}
            className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 text-sm font-medium"
          >
            Retry
          </button>
        </div>
      ) : isLoading ? (
        <div className="text-center py-12">
          <p className="text-gray-500">Loading...</p>
        </div>
      ) : showShelved ? (
        shelvedItems.length > 0 ? (
          <div className="bg-card rounded-lg shadow border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-gray-500 border-b">
                  <tr>
                    <th className="px-4 py-3">Building</th>
                    <th className="px-4 py-3">SS count</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3">
                      {contractFilter === 'ENDED' ? 'End date' : 'Start / resume'}
                    </th>
                    <th className="px-4 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {shelvedItems.map((row) => (
                    <tr key={row.building_id} className="hover:bg-slate-50">
                      <td className="px-4 py-3">
                        <Link
                          to={`/buildings/${row.building_id}`}
                          className="font-medium text-primary-800 hover:underline"
                        >
                          {row.building_name || row.building_id}
                        </Link>
                        {row.building_address ? (
                          <p className="text-xs text-gray-500 truncate max-w-xs">{row.building_address}</p>
                        ) : null}
                      </td>
                      <td className="px-4 py-3 text-gray-800 tabular-nums">
                        {Number(row.ss_count) > 0 ? Number(row.ss_count) : '—'}
                      </td>
                      <td className="px-4 py-3 text-xs font-semibold">
                        {row.contract_status === 'ENDED' ? 'Ended' : 'Starts later'}
                      </td>
                      <td className="px-4 py-3 text-gray-700 tabular-nums text-xs">
                        {row.contract_resume_date
                          ? String(row.contract_resume_date).slice(0, 10)
                          : '—'}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          type="button"
                          onClick={() => {
                            const name = row.building_name || 'this building';
                            if (
                              !window.confirm(
                                `Set "${name}" to Active?\n\nIt will show in All / Due / Paperwork again.`
                              )
                            ) {
                              return;
                            }
                            setClassifyBusy(`${row.building_id}:ACTIVE`);
                            classifyMutation.mutate({
                              buildingId: row.building_id,
                              status: 'ACTIVE',
                            });
                          }}
                          disabled={classifyMutation.isPending}
                          className="inline-flex items-center px-2.5 py-1.5 text-xs bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50"
                        >
                          <RotateCcw className="w-3.5 h-3.5 mr-1" />
                          {classifyBusy === `${row.building_id}:ACTIVE` ? '…' : 'Active'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <p className="text-center py-12 text-gray-500 text-sm">No buildings in this list.</p>
        )
      ) : buildings.length > 0 ? (
        <div className="bg-card rounded-lg shadow border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-gray-500 border-b">
                <tr>
                  <th className="px-4 py-3 w-12">#</th>
                  <th className="px-4 py-3">Building</th>
                  <th className="px-4 py-3">Scope</th>
                  <th className="px-4 py-3">Company / partners</th>
                  <th className="px-4 py-3">Due</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {buildings.map((building, idx) => {
                  const days = daysUntilDue(building.due_date);
                  const partners = building.partner_companies || [];
                  return (
                    <tr
                      key={building.id}
                      className="hover:bg-slate-50 cursor-pointer"
                      onClick={() => setSelected(building)}
                    >
                      <td className="px-4 py-3 text-gray-400 tabular-nums">{idx + 1}</td>
                      <td className="px-4 py-3">
                        <div className={`font-semibold ${getBuildingNameClass(days, building.application_approved)}`}>
                          {building.name}
                        </div>
                        {building.address && (
                          <div className="text-xs text-gray-500 truncate max-w-xs">{building.address}</div>
                        )}
                      </td>
                      <td className="px-4 py-3">{scopeBadge(building.agency_scope)}</td>
                      <td className="px-4 py-3 text-gray-700">
                        {partners.length
                          ? partners.join(', ')
                          : building.agency_scope === 'FULL_AGENT'
                            ? 'Full agent (us)'
                            : building.owner_name || '—'}
                        {building.ss_only_codes?.length > 0 && (
                          <div className="text-xs text-violet-700 mt-0.5">
                            SS: {building.ss_only_codes.join(', ')}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-gray-600">
                        {building.due_date ? new Date(building.due_date).toLocaleDateString() : '—'}
                      </td>
                      <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                        {showInactive || building.status === 'INACTIVE' ? (
                          <button
                            type="button"
                            onClick={() => {
                              if (
                                window.confirm(
                                  `Set "${building.name}" to Active again?\n\nIt will show in the portfolio and apps.`
                                )
                              ) {
                                restoreMutation.mutate(building.id);
                              }
                            }}
                            disabled={restoreMutation.isLoading}
                            className="inline-flex items-center px-2.5 py-1.5 text-xs bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50"
                          >
                            <RotateCcw className="w-3.5 h-3.5 mr-1" />
                            Active
                          </button>
                        ) : (
                          <div className="inline-flex flex-wrap justify-end gap-1.5">
                            <button
                              type="button"
                              onClick={() => {
                                if (!confirmClassifyBuilding(building.name, 'DEFERRED')) return;
                                setClassifyBusy(`${building.id}:DEFERRED`);
                                classifyMutation.mutate({
                                  buildingId: building.id,
                                  status: 'DEFERRED',
                                });
                              }}
                              disabled={classifyMutation.isLoading}
                              className="inline-flex items-center px-2.5 py-1.5 text-xs border border-sky-300 text-sky-900 rounded-lg hover:bg-sky-50 disabled:opacity-50"
                              title="Starts later — hide from work queues (data kept)"
                            >
                              <CalendarClock className="w-3.5 h-3.5 mr-1" />
                              {classifyBusy === `${building.id}:DEFERRED` ? '…' : 'Starts later'}
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                if (!confirmClassifyBuilding(building.name, 'ENDED')) return;
                                setClassifyBusy(`${building.id}:ENDED`);
                                classifyMutation.mutate({
                                  buildingId: building.id,
                                  status: 'ENDED',
                                });
                              }}
                              disabled={classifyMutation.isLoading}
                              className="inline-flex items-center px-2.5 py-1.5 text-xs border border-slate-300 text-slate-800 rounded-lg hover:bg-slate-50 disabled:opacity-50"
                              title="Ended — contract finished (data kept)"
                            >
                              <Ban className="w-3.5 h-3.5 mr-1" />
                              {classifyBusy === `${building.id}:ENDED` ? '…' : 'Ended'}
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className="px-4 py-2 text-xs text-gray-500 border-t bg-slate-50">
            {buildings.length} building{buildings.length === 1 ? '' : 's'} · sorted A-Z / numeric · click row for
            details
          </p>
        </div>
      ) : (
        <div className="text-center py-12 bg-card rounded-lg shadow">
          <Building2 className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500">
            {showInactive
              ? 'No inactive buildings.'
              : scope === 'full'
                ? 'No full-agent buildings.'
                : scope === 'ss_only'
                  ? 'No partly SS-only buildings.'
                  : 'No buildings found.'}
          </p>
          {showInactive ? (
            <button type="button" onClick={openPortfolio} className="mt-4 text-primary-600 hover:underline text-sm">
              Back to portfolio
            </button>
          ) : (
            <Link to="/buildings/new" className="mt-4 inline-block text-primary-600 hover:underline text-sm">
              New building
            </Link>
          )}
        </div>
      )}

      {selected && (
        <BuildingInfoPanel
          building={selected}
          onClose={() => setSelected(null)}
          onOpen={(id) => {
            setSelected(null);
            navigate(`/buildings/${id}`);
          }}
        />
      )}
    </div>
  );
}

export default Buildings;
